import { useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { useOmniOfferBaseDataCreateMutation } from "../api/base-data-api";

const emptyForm = { label: "", companyId: "" };

export function CreateOfferBaseDataDialog({
  serviceKind,
}: {
  serviceKind: string;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [create, { isLoading, error, reset }] =
    useOmniOfferBaseDataCreateMutation();

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setForm(emptyForm);
      reset();
    }
  };

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    create({ id: v4(), serviceKind, ...form })
      .unwrap()
      .then(() => {
        toast.success(t("Basisdaten erstellt"));
        onOpenChange(false);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CommonDialogTrigger icon={Plus} label={t("Basisdaten")} />
      <DialogContent>
        <CommonDialogHeader
          title={t("Basisdaten erstellen")}
          description={t(
            "Die Bezeichnung unterscheidet die Basisdaten eines Unternehmens voneinander. Die Vorgabewerte werden anschließend erfasst.",
          )}
        />
        <div className="space-y-4">
          <div className="grid content-start gap-1.5">
            <Label>{t("Bezeichnung")}</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            <InputValidationErrors error={error} field="label" />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("Unternehmen")}</Label>
            <CompanySelect
              companyId={form.companyId}
              onCompanyIdChange={(companyId) => setForm({ ...form, companyId })}
            />
            <InputValidationErrors error={error} field="companyId" />
          </div>
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}
